import React from "react";
import { Link, useParams } from "react-router-dom";
import { findArticle, ARTICLES } from "../data/articles";
import useSeo from "../hooks/useSeo";
import NewsletterCTA from "../components/NewsletterCTA";
import { Calendar, Clock, ArrowLeft, ArrowRight } from "lucide-react";

export default function BlogArticlePage() {
  const { slug } = useParams();
  const article = findArticle(slug);

  useSeo({
    title: article ? `${article.title} · EMA Kurer Blog` : "Artikel ikke fundet · EMA Kurer",
    description: article ? article.excerpt : "",
    path: `/blog/${slug || ""}`,
    image: article?.image,
  });

  if (!article) {
    return (
      <div className="bg-brand-bg min-h-screen py-20" data-testid="article-not-found">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h1 className="font-display text-3xl font-bold text-brand-dark mb-4">
            Artikel ikke fundet
          </h1>
          <Link to="/blog" className="text-brand-red font-semibold hover:underline">
            ← Tilbage til bloggen
          </Link>
        </div>
      </div>
    );
  }

  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  // BlogPosting JSON-LD for rich results
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": { "@type": "Organization", "name": "EMA Kurer ApS", "url": "https://emakurer.dk" },
    "publisher": {
      "@type": "Organization",
      "name": "EMA Kurer ApS",
      "logo": { "@type": "ImageObject", "url": "https://emakurer.dk/og-default.jpg" },
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://emakurer.dk/blog/${article.slug}` },
    "image": article.image ? [`https://emakurer.dk${article.image}`] : undefined,
    "keywords": (article.keywords || []).join(", "),
  };

  return (
    <div className="bg-brand-bg min-h-screen" data-testid={`article-page-${article.slug}`}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section className="bg-brand-dark text-white py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-5">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-6">
            <ArrowLeft size={14} /> Tilbage til bloggen
          </Link>
          <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-white/60 mb-3">
            <span className="font-bold text-brand-red">{article.category}</span>
            <span className="flex items-center gap-1"><Calendar size={12} />{article.date}</span>
            <span className="flex items-center gap-1"><Clock size={12} />{article.readMinutes} min læsetid</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            {article.title}
          </h1>
          <p className="mt-4 text-lg text-white/80">{article.excerpt}</p>
        </div>
      </section>

      {/* Body */}
      <article className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-5">
          <div className="prose-content space-y-5 text-brand-dark" data-testid="article-body">
            {article.body.map((block, i) => {
              if (block.h2) {
                return (
                  <h2 key={i} className="font-display text-2xl sm:text-3xl font-bold mt-8 mb-2">
                    {block.h2}
                  </h2>
                );
              }
              if (block.p) {
                return <p key={i} className="text-base leading-relaxed text-brand-text2">{block.p}</p>;
              }
              if (block.ul) {
                return (
                  <ul key={i} className="space-y-2 pl-5 list-disc text-brand-text2">
                    {block.ul.map((item, j) => (
                      <li key={j} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </div>

          {/* CTA */}
          <div className="mt-12 bg-brand-dark text-white rounded-2xl p-8 text-center">
            <h3 className="font-display text-2xl font-bold mb-2">Klar til at booke en kurer?</h3>
            <p className="text-white/80 mb-5">Direkte A → B på Sjælland — fra 199 kr inkl. 8 km.</p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red/90 text-white px-6 py-3 rounded-lg font-bold transition-colors"
              data-testid="article-cta-book"
            >
              Book nu <ArrowRight size={16} />
            </Link>
          </div>

          {/* Newsletter */}
          <div className="mt-8">
            <NewsletterCTA source={`blog_article_${article.slug}`} variant="inline" />
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-12 bg-white border-t border-slate-200">
          <div className="max-w-5xl mx-auto px-5">
            <h2 className="font-display text-2xl font-bold text-brand-dark mb-6">Læs også</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group block p-5 rounded-xl border border-slate-200 hover:border-brand-red transition-colors"
                  data-testid={`related-${r.slug}`}
                >
                  <div className="text-xs uppercase tracking-wider font-bold text-brand-red mb-2">{r.category}</div>
                  <h3 className="font-display text-lg font-bold text-brand-dark group-hover:text-brand-red transition-colors leading-snug">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-sm text-brand-text2 line-clamp-2">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
